/**
 * Typed calls for the App Experience page. The ONE file that names an API
 * path (pinned by tests/unit/admin/app-experience-wiring-lint.test.ts).
 *
 * Shapes mirror backend/src/routes/admin-app-experience.ts and
 * backend/src/app-experience/uploads.ts. The admin panel cannot import the
 * backend; the lint pins that the fields read here still exist there.
 *
 * Two transports, on purpose:
 *   - the API: `adminFetch` (the admin's token, errors as AdminApiError);
 *   - the file: an XMLHttpRequest straight to storage, for the progress bar.
 *     It sends the signed form and NO header of ours — the token must never
 *     go to the storage host — and it is never a native <form> submit (the
 *     panel's CSP has form-action 'none').
 */
import { adminFetch } from '@/providers/admin-api';
import type {
  AbbyWelcomeConfig,
  AppExperiencePublic,
  ExperienceLocale,
  ExperienceSurface,
  IntroVideoConfig,
  WelcomeVoiceLocale,
} from './lib/contract';
import { PlainWordsError } from './lib/error-words';
import { buildUploadForm, uploadErrorText, type UploadStatus } from './lib/upload-flow';

/** backend AdminVersionRow */
export interface AdminVersionRow {
  version: number;
  config: unknown;
  note: string | null;
  created_by_admin_id: string;
  /** ISO 8601 */
  created_at: string;
}

/** backend AdminAppExperienceView: read UNCACHED, so `history[s][0].version` is the true latest. */
export interface AdminAppExperienceView {
  live: AppExperiencePublic;
  history: Record<ExperienceSurface, AdminVersionRow[]>;
  media_origin: string;
}

export interface PublishBody {
  config: IntroVideoConfig | AbbyWelcomeConfig;
  /** The newest version the admin saw; null = nothing was published yet. A stale value is a 409. */
  based_on_version: number | null;
  note?: string | null;
}

/** backend UploadCreated */
export interface UploadCreated {
  upload_id: string;
  url: string;
  fields: Record<string, string>;
  expires_at: string;
}

/** One row of GET /v1/admin/admins — only the fields used to name an author. */
export interface AdminName {
  id: string;
  display_name: string | null;
  email: string | null;
}

const BASE = '/v1/admin/app-experience';

export async function fetchAdminView(apiUrl: string): Promise<AdminAppExperienceView> {
  const res = await adminFetch(`${apiUrl}${BASE}`);
  return (await res.json()) as AdminAppExperienceView;
}

export async function publishSurface(apiUrl: string, surface: ExperienceSurface, body: PublishBody): Promise<{ version: number }> {
  const res = await adminFetch(`${apiUrl}${BASE}/${surface}`, { method: 'POST', body: JSON.stringify(body) });
  return (await res.json()) as { version: number };
}

export async function createUpload(apiUrl: string, file: File): Promise<UploadCreated> {
  const res = await adminFetch(`${apiUrl}${BASE}/uploads`, {
    method: 'POST',
    body: JSON.stringify({ file_name: file.name, bytes: file.size, content_type: file.type || null }),
  });
  return (await res.json()) as UploadCreated;
}

export async function startProcessing(apiUrl: string, uploadId: string): Promise<UploadStatus> {
  const res = await adminFetch(`${apiUrl}${BASE}/uploads/${encodeURIComponent(uploadId)}/process`, { method: 'POST' });
  return (await res.json()) as UploadStatus;
}

export async function readUploadStatus(apiUrl: string, uploadId: string): Promise<UploadStatus> {
  const res = await adminFetch(`${apiUrl}${BASE}/uploads/${encodeURIComponent(uploadId)}`);
  return (await res.json()) as UploadStatus;
}

/** backend SynthesizedParagraph (app-experience/tts.ts): one paragraph's voice; `cached` = no new cost. */
export interface SynthesizedParagraph {
  key: string;
  url: string;
  cached: boolean;
}

/** Abby's voice for ONE paragraph. The server trims the text and refuses a line break or a hidden character (the contract's text rule). */
export async function synthesizeWelcomeParagraph(
  apiUrl: string,
  text: string,
  use: { locale: ExperienceLocale; index: number },
): Promise<SynthesizedParagraph> {
  // `locale` and `paragraph_index` go to the server's audit row of this call only.
  const res = await adminFetch(`${apiUrl}${BASE}/welcome/tts`, {
    method: 'POST',
    body: JSON.stringify({ text, locale: use.locale, paragraph_index: use.index }),
  });
  return (await res.json()) as SynthesizedParagraph;
}

/**
 * One recording for a language, with the exact paragraph timings. A 409 body
 * carries `missing`: the paragraphs (from 0) that have no voice yet.
 */
export async function assembleWelcomeVoice(
  apiUrl: string,
  locale: ExperienceLocale,
  paragraphs: string[],
): Promise<WelcomeVoiceLocale> {
  const res = await adminFetch(`${apiUrl}${BASE}/welcome/assemble`, {
    method: 'POST',
    body: JSON.stringify({ locale, paragraphs }),
  });
  return (await res.json()) as WelcomeVoiceLocale;
}

export async function fetchAdminNames(apiUrl: string): Promise<AdminName[]> {
  const res = await adminFetch(`${apiUrl}/v1/admin/admins`);
  return (await res.json()) as AdminName[];
}

/** Thrown when the admin cancels the transfer. Not an error to show. */
export class UploadCancelled extends Error {
  constructor() {
    super('The upload was cancelled.');
    this.name = 'UploadCancelled';
  }
}

/**
 * Send the file to storage with the signed form. `done` resolves when storage
 * accepts the file, and rejects with words an admin can act on, or with
 * UploadCancelled after `abort()`.
 */
export function sendToStorage(
  policy: Pick<UploadCreated, 'url' | 'fields'>,
  file: File,
  onProgress: (loaded: number, total: number) => void,
): { done: Promise<void>; abort: () => void } {
  const xhr = new XMLHttpRequest();
  const done = new Promise<void>((resolve, reject) => {
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded, e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new PlainWordsError(uploadErrorText(xhr.status, xhr.responseText ?? '')));
    };
    xhr.onerror = () => reject(new PlainWordsError(uploadErrorText(0, '')));
    xhr.onabort = () => reject(new UploadCancelled());
  });
  xhr.open('POST', policy.url);
  xhr.send(buildUploadForm(policy.fields, file));
  return { done, abort: () => xhr.abort() };
}
